import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardMedia,
  IconButton,
  FormControlLabel,
  Switch
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import {
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx,
  dialogFormActionsSx,
  uploadDropzoneSx,
  uploadDropzoneCloudIconSx
} from '../utils/dialogFormLayout';

const CurrencyPackForm = ({ open, onClose, onSubmit, currencyPack = null, isEdit = false }) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [tokens, setTokens] = useState('');
  const [image, setImage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const fileInputRef = useRef();

  // Update form fields when currencyPack prop changes
  useEffect(() => {
    if (currencyPack) {
      setName(currencyPack.name || '');
      setPrice(currencyPack.price || '');
      setTokens(currencyPack.tokens || '');
      setImage(currencyPack.image || '');
      setImageFile(null);
      setIsActive(currencyPack.isActive !== false);
    } else {
      setName('');
      setPrice('');
      setTokens('');
      setImage('');
      setImageFile(null);
      setIsActive(true);
    }
  }, [currencyPack]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() && price !== '' && tokens !== '') {
      onSubmit({ 
        name: name.trim(),
        price: Number(price),
        tokens: Number(tokens),
        imageFile,
        existingImage: image && !imageFile ? image : null,
        isActive
      });
      setName('');
      setPrice('');
      setTokens('');
      setImage('');
      setImageFile(null);
      setIsActive(true);
      onClose();
    }
  };

  const handleClose = () => {
    setName('');
    setPrice('');
    setTokens('');
    setImage('');
    setImageFile(null);
    setIsActive(true);
    onClose();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      setImage(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: dialogFormPaperSx }}
    >
      <DialogTitle sx={dialogFormTitleSx}>
        {isEdit ? 'Edit Currency Pack' : 'Create New Currency Pack'}
      </DialogTitle>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
          <TextField
            autoFocus
            margin="dense"
            label="Pack Name"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Price"
            type="number"
            fullWidth
            variant="outlined"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Tokens"
            type="number"
            fullWidth
            variant="outlined"
            value={tokens}
            onChange={(e) => setTokens(e.target.value)}
            required
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />

          {/* Image Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Pack Image
            </Typography>
            {image ? (
              <Card sx={{ mb: 2, maxWidth: 200 }}>
                <CardMedia
                  component="img"
                  height="150"
                  image={image}
                  alt="Pack image"
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {imageFile ? imageFile.name : 'Pack image'}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveImage} color="error">
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            ) : (
              <Box sx={(theme) => uploadDropzoneSx(theme)} onClick={openFileDialog}>
                <CloudUpload sx={uploadDropzoneCloudIconSx} />
                <Typography variant="body1" color="text.secondary">
                  Click to upload pack image
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports: JPG, PNG, GIF
                </Typography>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Active"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={dialogFormActionsSx}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CurrencyPackForm;
